import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://optionwizard.org';
const DEFAULTS = {
  title: 'Option Wizard | Stock Options Strategy Assistant',
  description: 'Option Wizard — Stock Options Strategy Assistant. Analyze stock options positions, evaluate strategies, compare roll adjustments and understand profit-and-loss scenarios. Free Daily Pick and interactive demo.',
  ogTitle: 'Option Wizard — Stock Options Strategy Assistant',
  ogDescription: 'Analyze stock options positions, evaluate strategies, compare roll adjustments and understand profit-and-loss scenarios in one platform.',
  image: `${ORIGIN}/og-image.jpg`,
  type: 'website',
};

// Per-route metadata for PUBLIC pages. Private areas are blocked in robots.txt and keep the defaults.
const PAGES = {
  '/': {},
  '/about': {
    title: 'About Option Wizard | Methodology and Disclosures',
    description: 'What Option Wizard is, how its options calculations work (Black-Scholes valuation, Greeks, breakevens, max profit/loss, scenario analysis, bid/ask-based roll ranking), data limitations and risk disclosures.',
    type: 'article',
  },
  '/demo': { title: 'Interactive Demo | Option Wizard', description: 'Try the Position Analyzer, Rollmaster and Roll Comparison on clearly labelled synthetic data. Demo mode — not live market quotes, not executable.' },
  '/picks': { title: 'Daily Pick | Option Wizard', description: 'Free daily, AI-assisted list of stocks with option-strategy ideas and educational context, plus a transparent public record of past picks.' },
  '/contact': { title: 'Contact | Option Wizard' },
  '/legal': { title: 'Legal Center | Option Wizard' },
  '/terms': { title: 'Terms of Service | Option Wizard' },
  '/privacy': { title: 'Privacy Policy | Option Wizard' },
  '/refund-policy': { title: 'Refund Policy | Option Wizard' },
  '/safety': { title: 'Safety Policy | Option Wizard' },
  '/code-of-conduct': { title: 'Code of Conduct | Option Wizard' },
  '/acceptable-use': { title: 'Acceptable Use Policy | Option Wizard' },
};

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    const m = selector.match(/\[(\w+(?::\w+)?)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

export const applyRouteSeo = (pathname) => {
  const page = PAGES[pathname];
  const isPublic = page !== undefined;
  const meta = { ...DEFAULTS, ...(page || {}) };
  const url = `${ORIGIN}${pathname === '/' ? '/' : pathname}`;
  document.title = meta.title;
  setMeta('meta[name="description"]', { content: meta.description });
  setMeta('link[rel="canonical"]', { href: url });
  setMeta('meta[property="og:url"]', { content: url });
  setMeta('meta[property="og:type"]', { content: meta.type });
  setMeta('meta[property="og:title"]', { content: page?.title || meta.ogTitle });
  setMeta('meta[property="og:description"]', { content: page?.description || meta.ogDescription });
  setMeta('meta[property="og:image"]', { content: meta.image });
  setMeta('meta[name="twitter:card"]', { content: 'summary_large_image' });
  setMeta('meta[name="twitter:title"]', { content: page?.title || meta.ogTitle });
  setMeta('meta[name="twitter:description"]', { content: page?.description || meta.ogDescription });
  setMeta('meta[name="twitter:image"]', { content: meta.image });
  // Homepage-only structured data: keep the JSON-LD active only on "/"
  document.querySelectorAll('script[data-home-ld]').forEach((s) => {
    s.type = pathname === '/' ? 'application/ld+json' : 'application/ld+json;inactive';
  });
  return isPublic;
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => { applyRouteSeo(pathname); }, [pathname]);
  return null;
};

export default RouteSeo;
